/*
 * Copyright (c) 2022-2023. Haodong JU
 */

import readXlsxFile from 'read-excel-file'
import hillo from 'hillo'
import sortBy from 'lodash-es/sortBy'
import { groupBy } from 'lodash-es'
import Category from '@/model/dataLayer/api/dish/Category'
import PrinterGroup from '@/model/dataLayer/api/print/PrinterGroup'
import Dish from '@/model/dataLayer/api/dish/Dish'
import IKUtils from 'innerken-js-utils'
import { showSuccessMessage } from '@/model/utils/utils'
import i18n from '@/i18n'
import Allergen from '@/model/dataLayer/api/dish/Allergen'
import store from '@/model/global/store'
import Attribute from '@/model/dataLayer/api/dish/Attribute'
import AttributeGroup from '@/model/dataLayer/api/dish/AttributeGroup'

async function getDCTList () {
  const res = []
  for (const lang of ['ZH', 'DE', 'EN']) {
    res.push((await hillo.get('CategoryType.php', { lang })).content)
  }
  return Object.values(groupBy(res.flat(), 'id'))
}

async function getAttributeGroupList () {
  const res = []
  for (const lang of ['ZH', 'DE', 'EN']) {
    res.push((await hillo.get('Category.php?op=showAttributeGroup', { lang })).content)
  }
  return Object.values(groupBy(res.flat(), 'id'))
}

async function getAttributeList () {
  const res = []
  for (const lang of ['ZH', 'DE', 'EN']) {
    res.push((await hillo.get('Category.php?op=showAttribute', { lang })).content)
  }
  return Object.values(groupBy(res.flat(), 'id'))
}

async function getDCTIdByName (name) {
  const dctList = await getDCTList()
  return parseInt(dctList.find(i => i.map(it => it.name).includes(name))?.[0]?.id ?? -1)
}

async function getAttributeGroupIdByName (name) {
  const dctList = await getAttributeGroupList()
  return parseInt(dctList.find(i => i.map(it => it.name).includes(name))?.[0]?.id ?? -1)
}

async function getAttributeIdByName (name) {
  const dctList = await getAttributeList()
  return parseInt(dctList.find(i => i.map(it => it.name).includes(name))?.[0]?.id ?? -1)
}

function dishesCategoryHash (dc) {
  dc.langs = sortBy(dc.langs, 'lang')
  return dc.dishesCategoryTypeId + '%' + dc.langs.reduce((str, i) => str + i.name, '')
}

function dishesAttributeGroupHash (dc) {
  dc.langs = sortBy(dc.langs, 'lang')
  return dc.id + '%' + dc.langs.reduce((str, i) => str + i.name, '')
}

function dishesAttributeHash (dc) {
  dc.langs = sortBy(dc.langs, 'lang')
  return dc.id + '%' + dc.langs.reduce((str, i) => str + i.name, '')
}

export async function convertFileData (inputFile) {
  IKUtils.showLoading(true)
  // key值为非中文最大程度避免代码转义过程的错误
  const fetchedDishes = []
  const fetchedPrinter = {}
  const fetchedAllergy = {}
  const fetchedDishesCategory = {}
  const errors = []

  for (const row of inputFile) {
    // create dishCategory
    const dishCategory = {
      printOrder: 50,
      categorySort: 50,
      color: '#F8BBD0',
      langs: [
        {
          name: row['Category (Chinese)'],
          lang: 'ZH'
        }, {
          name: row['Category (German)'],
          lang: 'DE'
        }, {
          name: row['Category (English)'],
          lang: 'EN'
        }
      ],
      dishesCategoryTypeId: await getDCTIdByName(row['Tax Type'])
    }
    console.log(row)
    let fakeCategoryId = Object.values(fetchedDishesCategory)
      .find(it => dishesCategoryHash(it) === dishesCategoryHash(dishCategory))?.fakeCategoryId ?? -1

    if (fakeCategoryId === -1) {
      fakeCategoryId = 'fakeCategoryId_' + Object.values(fetchedDishesCategory).length

      fetchedDishesCategory[Object.values(fetchedDishesCategory).length] = {
        ...dishCategory,
        realId: null,
        fakeCategoryId
      }
    }
    // create printer
    const printerName = row.Printer
    let fakePrinterId = Object.values(fetchedPrinter).find(it => it.name === printerName)?.fakePrinterId ?? -1

    const printer = {
      name: printerName,
      isSingleLinePrint: true,
      isSingleOrderPrint: false,
      upsideDown: false,
      defaultRetryCount: '0',
      isReceptionPrinter: false
    }
    if (fakePrinterId === -1) {
      fakePrinterId = 'fakePrinterGroupId_' + Object.values(fetchedPrinter).length

      fetchedPrinter[Object.values(fetchedPrinter).length] = {
        ...printer,
        realId: null,
        fakePrinterId
      }
    }

    // create Allergy
    const allergyName = row.Allergy
    let fakeAllergyId = Object.values(fetchedAllergy).find(it => it.name === allergyName)?.fakeAllergyId ?? -1

    const allergy = {
      name: allergyName
    }
    if (fakeAllergyId === -1) {
      fakeAllergyId = 'fakePrinterGroupId_' + Object.values(fetchedAllergy).length

      fetchedAllergy[Object.values(fetchedAllergy).length] = {
        ...allergy,
        realId: null,
        fakeAllergyId
      }
    }

    // create dishes
    const langGroup = [
      {
        name: row['Name (Chinese)'],
        lang: 'ZH',
        desc: row.Description
      }, {
        name: row['Name (German)'],
        lang: 'DE',
        desc: row.Description
      }, {
        name: row['Name (English)'],
        lang: 'EN',
        desc: row.Description
      }
    ]

    fetchedDishes.push({
      code: row['Product Number'].toString().toLowerCase(),
      price: row.Price,
      langs: langGroup,
      fakePrinterId,
      fakeCategoryId,
      fakeAllergyId
    })
  }

  const categoryList = await Category.getList()
  const printerGroupList = await PrinterGroup.getList()
  const dishList = await Dish.getList()
  const allergyList = (await Allergen.getList())

  if (errors.length > 0) {
    return {
      state: 'bad',
      errors
    }
  }
  for (const key in fetchedDishesCategory) {
    const res = categoryList.find(j => dishesCategoryHash(fetchedDishesCategory[key]) === dishesCategoryHash(j))
    fetchedDishesCategory[key].realId = res?.id
    fetchedDishesCategory[key].add = !res
  }

  for (const key in fetchedPrinter) {
    const res = printerGroupList.find(j => fetchedPrinter[key].name === j.name)
    fetchedPrinter[key].realId = res?.id
    fetchedPrinter[key].add = !res
  }

  for (const key in fetchedAllergy) {
    if (fetchedAllergy[key].name) {
      const res = allergyList.find(j => fetchedAllergy[key].name === j.name)
      fetchedAllergy[key].realId = res?.id
      fetchedAllergy[key].add = !res
    }
  }

  for (const key in fetchedDishes) {
    const res = dishList.find(j => fetchedDishes[key].code.toString().toLowerCase() === j.code)
    fetchedDishes[key].id = res?.id
    fetchedDishes[key].add = !res
  }

  showSuccessMessage(i18n.t('edit_success'))

  return {
    state: 'good',
    fetchedData: {
      categoryDict: fetchedDishesCategory,
      printerDict: fetchedPrinter,
      dishList: fetchedDishes,
      allergyDict: fetchedAllergy
    }
  }
}

export async function convertAttributeFileData (inputFile) {
  IKUtils.showLoading(true)
  // key值为非中文最大程度避免代码转义过程的错误
  const fetchedAttribute = []
  const fetchedAttributeGroup = {}
  const errors = []

  for (const row of inputFile) {
    // create attributeGroup
    const attributeGroup = {
      langs: [
        {
          name: row['Attribute Group (Chinese)'],
          lang: 'ZH'
        }, {
          name: row['Attribute Group (German)'],
          lang: 'DE'
        }, {
          name: row['Attribute Group (English)'],
          lang: 'EN'
        }
      ],
      id: await getAttributeGroupIdByName(row['Attribute Group (German)'])
    }
    let fakeAttributeGroupId = Object.values(fetchedAttributeGroup)
      .find(it => dishesAttributeGroupHash(it) === dishesAttributeGroupHash(attributeGroup))?.fakeAttributeGroupId ?? -1

    if (fakeAttributeGroupId === -1) {
      fakeAttributeGroupId = 'fakeCategoryId_' + Object.values(fetchedAttributeGroup).length

      fetchedAttributeGroup[Object.values(fetchedAttributeGroup).length] = {
        ...attributeGroup,
        realId: null,
        fakeAttributeGroupId
      }
    }
    // create attribute
    const attribute = {
      langs: [
        {
          name: row['Attribute (Chinese)'],
          lang: 'ZH'
        }, {
          name: row['Attribute (German)'],
          lang: 'DE'
        }, {
          name: row['Attribute (English)'],
          lang: 'EN'
        }
      ],
      printMod: row['Attribute (German)'],
      priceMod: row.Price,
      fakeAttributeGroupId,
      id: await getAttributeIdByName(row['Attribute (German)']),
      dishesCategoryTypeId: await getDCTIdByName(row['Tax Type'])
    }
    let fakeAttributeId = Object.values(fetchedAttribute)
      .find(it => dishesAttributeHash(it) === dishesAttributeHash(attribute))?.fakeAttributeId ?? -1

    if (fakeAttributeId === -1) {
      fakeAttributeId = 'fakeCategoryId_' + Object.values(fetchedAttribute).length

      fetchedAttribute[Object.values(fetchedAttribute).length] = {
        ...attribute,
        realId: null,
        fakeAttributeId
      }
    }
  }

  const attributeList = await Attribute.getList()
  const attributeGroupList = await AttributeGroup.getList()

  if (errors.length > 0) {
    return {
      state: 'bad',
      errors
    }
  }
  for (const key in fetchedAttributeGroup) {
    const res = attributeGroupList.find(
      j => dishesAttributeGroupHash(fetchedAttributeGroup[key]) === dishesAttributeGroupHash(j))
    fetchedAttributeGroup[key].realId = res?.id
    fetchedAttributeGroup[key].add = !res
  }
  for (const key in fetchedAttribute) {
    const res = attributeList.find(j => dishesAttributeHash(fetchedAttribute[key]) === dishesAttributeHash(j))
    fetchedAttribute[key].id = res?.id
    fetchedAttribute[key].add = !res
  }

  showSuccessMessage(i18n.t('edit_success'))

  return {
    state: 'good',
    fetchedData: {
      attributeGroup: fetchedAttributeGroup,
      attribute: fetchedAttribute
    }
  }
}

export async function uploadDish (fetchedDishesCategory, fetchedPrinter, fetchedDishes, fetchedAllergy) {
  IKUtils.showLoading(true)
  for (const key in fetchedDishesCategory) {
    if (fetchedDishesCategory[key].add) {
      const res = await Category.add(fetchedDishesCategory[key])
      fetchedDishesCategory[key].add = !res
      fetchedDishesCategory[key].realId = res?.content?.id
    }
  }
  for (const key in fetchedPrinter) {
    if (fetchedPrinter[key].add) {
      const res = await PrinterGroup.add(fetchedPrinter[key])
      fetchedPrinter[key].add = !res
      fetchedPrinter[key].realId = res
    }
  }
  for (const key in fetchedAllergy) {
    if (fetchedAllergy[key].add) {
      const res = (await Allergen.add(fetchedAllergy[key]))?.content?.id
      fetchedAllergy[key].add = !res
      fetchedAllergy[key].realId = res
    }
  }
  const updateDishList = []
  for (const key in fetchedDishes) {
    fetchedDishes[key].categoryId = await convertFakeCategoryId(fetchedDishes[key].fakeCategoryId,
      fetchedDishesCategory)
    fetchedDishes[key].printGroupId = await convertFakePrinterId(fetchedDishes[key].fakePrinterId, fetchedPrinter)
    fetchedDishes[key].AllergenId = await convertFakeAllergyId(fetchedDishes[key].fakeAllergyId, fetchedAllergy)
    fetchedDishes[key].allergenGroup = fetchedDishes[key].AllergenId

    if (fetchedDishes[key].add) {
      await Dish.add(fetchedDishes[key])
    } else {
      updateDishList.push(fetchedDishes[key])
      // await Dish.edit(fetchedDishes[key])
    }
  }
  if (updateDishList.length === 0) {
    showSuccessMessage(this.$t('ProductImportSuccessful'))
  } else {
    IKUtils.toast(this.$t('ProductImportFailed'), 'info')
    store.commit('showErrorDishDialog', {
      title: this.$t('ProductImportSameItemNumber'),
      message: updateDishList,
      value: 'dish'
    })
  }
}

export async function uploadAttribute (fetchedAttributeGroup, fetchedAttribute) {
  IKUtils.showLoading(true)
  for (const key in fetchedAttributeGroup) {
    if (fetchedAttributeGroup[key].add) {
      const res = await AttributeGroup.add(fetchedAttributeGroup[key])
      fetchedAttributeGroup[key].add = !res
      fetchedAttributeGroup[key].realId = res?.content?.id
    }
  }
  const updateAttributeList = []
  for (const key in fetchedAttribute) {
    fetchedAttribute[key].attributeGroupId = await convertFakeAttributeGroupId(
      fetchedAttribute[key].fakeAttributeGroupId, fetchedAttributeGroup)

    if (fetchedAttribute[key].add) {
      await Attribute.add(fetchedAttribute[key])
    } else {
      updateAttributeList.push(fetchedAttribute[key])
    }
  }
  if (updateAttributeList.length === 0) {
    showSuccessMessage(this.$t('ImportSuccessful'))
  } else {
    IKUtils.toast(this.$t('ImportFailed'), 'info')
    store.commit('showErrorDishDialog', {
      title: this.$t('ImportRefreshObjectName'),
      message: updateAttributeList,
      value: 'attribute'
    })
  }
}

export async function convertFakeCategoryId (fakeId, fetchedDishesCategory) {
  for (const key in fetchedDishesCategory) {
    if (fetchedDishesCategory[key].fakeCategoryId === fakeId) {
      return fetchedDishesCategory[key].realId
    }
  }
}

export async function convertFakePrinterId (fakeId, fetchedPrinter) {
  for (const key in fetchedPrinter) {
    if (fetchedPrinter[key].fakePrinterId === fakeId) {
      return fetchedPrinter[key].realId
    }
  }
}

export async function convertFakeAllergyId (fakeId, fetchedAllergy) {
  for (const key in fetchedAllergy) {
    if (fetchedAllergy[key].name) {
      if (fetchedAllergy[key].fakeAllergyId === fakeId) {
        return fetchedAllergy[key].realId
      }
    } else {
      return ''
    }
  }
}

export async function convertFakeAttributeGroupId (fakeId, fetchedAttributeGroup) {
  for (const key in fetchedAttributeGroup) {
    if (fetchedAttributeGroup[key].fakeAttributeGroupId === fakeId) {
      return fetchedAttributeGroup[key].realId
    }
  }
}

export async function parseExcel (file) {
  const res = await readXlsxFile(file)
  const header = res[2]
  return res.splice(4).map(body => {
    return Object.assign(...header.map((k, i) => ({ [k]: body[i] })))
  })
}

export async function checkFileType (file) {
  return (file && file?.name && ((file.name.split('.').pop().toLowerCase() === 'xls') || file.name.split('.').pop()
    .toLowerCase() === 'xlsx'))
}
